import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

const ThemeContext = createContext();

const STORAGE_KEY = "relinq.theme";

const readStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return window.localStorage.getItem(STORAGE_KEY) || "light";
};

// Páginas voltadas ao cliente final (formulários, agendamento, política) têm
// identidade própria e nunca entram no tema escuro, mesmo que quem as abra
// tenha escolhido o escuro neste navegador. A escolha salva não é alterada.
const PUBLIC_LIGHT_PREFIXES = ["/form/", "/card-form/", "/agendar/", "/agendamento/", "/politica-privacidade"];
const isPublicLightRoute = () =>
  typeof window !== "undefined" &&
  PUBLIC_LIGHT_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));

// Escreve o tema no <html>/<body> (o que o CSS lê).
const applyThemeToDom = (theme) => {
  const applied = isPublicLightRoute() ? "light" : theme;
  const root = document.documentElement;
  if (root) {
    root.dataset.theme = applied;
    // Páginas públicas ficam fora dos ajustes de contraste do tema claro.
    if (isPublicLightRoute()) root.dataset.public = "1";
    else delete root.dataset.public;
  }
  document.body.dataset.theme = applied;
  // Barra do navegador e controles nativos acompanham o tema (escuro = #221f2c, o "chrome").
  const isDark = applied === "dark";
  if (root) root.style.colorScheme = isDark ? "dark" : "";
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    if (!meta.dataset.lightColor) meta.dataset.lightColor = meta.getAttribute("content") || "#000000";
    meta.setAttribute("content", isDark ? "#221f2c" : meta.dataset.lightColor);
  }
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const FADE_CLASS = "rs-theme-fade";
const FADE_MS = 700;

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const fadeTimerRef = useRef(null);

  // Troca de tema suave: cross-fade da tela inteira (View Transitions API);
  // sem suporte, o <html> ganha uma classe que anima cor/fundo/borda por 700 ms.
  // A primeira aplicação (carga da página) não anima.
  const setTheme = useCallback((next) => {
    const target = typeof next === "function" ? next(themeRef.current) : next;
    if (target === themeRef.current) return;
    if (prefersReducedMotion()) {
      setThemeState(target);
      return;
    }
    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(() => {
        // O DOM precisa estar no tema novo quando o navegador captura o "depois".
        applyThemeToDom(target);
        flushSync(() => setThemeState(target));
      });
      return;
    }
    const root = document.documentElement;
    root.classList.add(FADE_CLASS);
    void root.offsetWidth; // a classe vale antes de a cor mudar
    setThemeState(target);
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(
      () => root.classList.remove(FADE_CLASS),
      FADE_MS,
    );
  }, []);

  useEffect(() => () => clearTimeout(fadeTimerRef.current), []);

  useEffect(() => {
    applyThemeToDom(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (error) {
      // ignore (private mode, etc.)
    }
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((current) => (current === "dark" ? "light" : "dark")),
    [setTheme],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

export default ThemeContext;
